import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';

// Fonction pour créer l'image à partir d'une URL
// Note: L'image est TOUJOURS une blob URL locale ou une data URL
// car ImageUpload.js pré-charge les URLs externes en blob local
const createImage = (url) =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = (error) => {
            console.error('Erreur chargement image:', error);
            reject(error);
        };
        image.src = url;
    });

// Fonction pour créer l'image croppée
const createCroppedImage = async (imageSrc, pixelCrop) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
        throw new Error('Impossible de créer le contexte canvas');
    }

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // PNG supporte la transparence, pas besoin de fond blanc

    ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
    );

    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) {
                resolve(blob);
            } else {
                reject(new Error('Canvas toBlob a retourné null - image corrompue ou CORS bloqué'));
            }
        }, 'image/png');
    });
};

const ImageCropModal = ({
    isOpen,
    onClose,
    imageSrc,
    onCropComplete,
    aspectRatio = 1,
    title = "Recadrer l'image"
}) => {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [loading, setLoading] = useState(false);

    const onCropCompleteCallback = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleSave = async () => {
        if (!croppedAreaPixels) return;

        setLoading(true);
        try {
            const croppedBlob = await createCroppedImage(imageSrc, croppedAreaPixels);
            await onCropComplete(croppedBlob);
            onClose();
        } catch (error) {
            console.error('Erreur crop:', error);
            alert('Erreur lors du recadrage');
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setCrop({ x: 0, y: 0 });
        setZoom(1);
    };

    if (!isOpen || !imageSrc) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl w-full max-w-2xl mx-4 overflow-hidden shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b">
                    <h2 className="text-xl font-bold flex items-center gap-2">
                        <span>✂️</span>
                        <span>{title}</span>
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
                    >
                        ×
                    </button>
                </div>

                {/* Zone de crop - hauteur fixe avec positionnement absolu pour react-easy-crop */}
                <div
                    className="bg-gray-900"
                    style={{
                        position: 'relative',
                        height: '400px',
                        width: '100%'
                    }}
                >
                    <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        minZoom={0.5}
                        maxZoom={3}
                        aspect={aspectRatio}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onCropComplete={onCropCompleteCallback}
                        objectFit="contain"
                        showGrid={true}
                        style={{
                            containerStyle: {
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0
                            }
                        }}
                    />
                </div>

                {/* Contrôles zoom */}
                <div className="p-4 bg-gray-50">
                    <div className="flex items-center gap-4">
                        <span className="text-lg">🔍</span>
                        <input
                            type="range"
                            min={0.5}
                            max={3}
                            step={0.1}
                            value={zoom}
                            onChange={(e) => setZoom(Number(e.target.value))}
                            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-orange-500"
                        />
                        <span className="text-lg">🔍+</span>
                        <button
                            onClick={handleReset}
                            className="px-3 py-1 text-sm bg-gray-200 rounded-lg hover:bg-gray-300 font-medium"
                        >
                            ↺ Reset
                        </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-2 text-center">
                        Zoom: {zoom.toFixed(1)}x • Glissez l'image pour la repositionner
                    </p>
                </div>

                {/* Boutons */}
                <div className="flex gap-3 p-4 border-t">
                    <button
                        onClick={onClose}
                        className="flex-1 py-3 px-4 bg-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-300 transition-colors"
                        disabled={loading}
                    >
                        Annuler
                    </button>
                    <button
                        onClick={handleSave}
                        className="flex-1 py-3 px-4 bg-green-500 text-white rounded-xl font-medium hover:bg-green-600 flex items-center justify-center gap-2 transition-colors"
                        disabled={loading}
                    >
                        {loading ? (
                            <span>⏳ Traitement...</span>
                        ) : (
                            <>
                                <span>✅</span>
                                <span>Valider</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ImageCropModal;
